import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

export const Logo = ({ dark = false }) => (
  <div className="flex items-center gap-3" data-testid="brand-logo">
    <img src="/nova-logo.png" alt="N Murthy Groups" className="h-10 w-auto shrink-0" />
    <div className="leading-none">
      <span className={`block font-serif-display text-lg font-600 tracking-tight ${dark ? "text-[var(--cream)]" : "text-[var(--ink)]"}`}>
        N Murthy <span className="text-[var(--gold)]">Groups</span>
      </span>
      <span className={`block text-[10px] uppercase tracking-[0.35em] ${dark ? "text-[var(--cream-3)]" : "text-[var(--ink-soft)]"}`}>
        Nova Royale
      </span>
    </div>
  </div>
);

const links = [
  { label: "The Layout", href: "#welcome" },
  { label: "Infrastructure", href: "#infrastructure" },
  { label: "Amenities", href: "#amenities" },
  { label: "Plots", href: "#plots" },
  { label: "Master Plan", href: "#master-plan" },
  { label: "Gallery", href: "#gallery" },
  { label: "Location", href: "#location" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-[var(--cream-3)]/60 backdrop-blur-xl"
      style={{ background: "rgba(250,250,248,0.82)" }}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
        <button onClick={() => scrollTo("#top")} data-testid="nav-logo-btn">
          <Logo />
        </button>
        <nav className="hidden items-center gap-9 lg:flex">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => scrollTo(l.href)}
              className="link-underline text-sm tracking-wide text-[var(--ink-soft)] transition-colors hover:text-[var(--ink)]"
              data-testid={`nav-${l.href.replace("#", "")}`}
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button
            onClick={() => scrollTo("#enquiry")}
            className="rounded-sm bg-[var(--ink)] px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-[var(--cream)] transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[var(--gold-hover)]"
            data-testid="nav-enquire-btn"
          >
            Enquire
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            className="flex h-10 w-10 items-center justify-center rounded-sm border border-[var(--cream-3)] text-[var(--ink)] lg:hidden"
            aria-label="Open menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-[var(--cream-3)]/60 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-5 py-2">
              {links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => scrollTo(l.href)}
                  className="border-b border-[var(--cream-3)]/50 py-3.5 text-left text-sm uppercase tracking-[0.15em] text-[var(--ink-soft)] transition-colors hover:text-[var(--ink)]"
                  data-testid={`nav-mobile-${l.href.replace("#", "")}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
